// src/features/clinical/index.jsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ClinicalProvider, useClinical } from './context/ClinicalContext';

// Pages
import ClinicalDashboard from './pages/ClinicalDashboard';
import PatientRegistrationPage from './pages/PatientRegistrationPage';
import NewVisitPage from './pages/NewVisitPage';
import PrescriptionPage from './pages/PrescriptionPage';
import ClinicalWorkflowPage from './pages/ClinicalWorkflowPage';
import PatientListPage from './pages/PatientListPage';
import VisitHistoryPage from './pages/VisitHistoryPage';
import PrescriptionHistoryPage from './pages/PrescriptionHistoryPage';

// Components (export for external use)
import PatientRegistration from './components/patient/PatientRegistration';
import PatientSearch from './components/patient/PatientSearch';
import PatientList from './components/patient/PatientList';
import VisitCreation from './components/visit/VisitCreation';
import VitalsForm from './components/visit/VitalsForm';
import VisitHistory from './components/visit/VisitHistory';
import PrescriptionCreation from './components/prescription/PrescriptionCreation';
import MedicineSelector from './components/prescription/MedicineSelector';
import TestSelector from './components/prescription/TestSelector';
import PrescriptionView from './components/prescription/PrescriptionView';
import DoctorNotes from './components/common/DoctorNotes';
import Timeline from './components/common/Timeline';
import LoadingSpinner from './components/common/LoadingSpinner';

// Layout Component
const ClinicalLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-gray-800">Clinical Management</h1>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <a href="/clinical" className="border-blue-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Dashboard
                </a>
                <a href="/clinical/patients" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Patients
                </a>
                <a href="/clinical/visits" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Visits
                </a>
                <a href="/clinical/prescriptions" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Prescriptions
                </a>
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-500">Dr. Smith</span>
            </div>
          </div>
        </div>
      </nav>
      <main className="py-6">
        {children}
      </main>
    </div>
  );
};

// Main Content Component with Loading State
const ClinicalContent = () => {
  const { loading, error } = useClinical();

  if (loading) {
    return (
     // <ClinicalLayout>
        <LoadingSpinner />
     //s </ClinicalLayout>
    );
  }

  if (error) {
    return (
    //  <ClinicalLayout>
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
     // </ClinicalLayout>
    );
  }

  return (
    // <ClinicalLayout>
      <Routes>
        <Route path="/" element={<ClinicalDashboard />} />
        <Route path="/dashboard" element={<ClinicalDashboard />} />
        
        {/* Patient Routes */}
        <Route path="/patients" element={<PatientListPage />} />
        <Route path="/patients/register" element={<PatientRegistrationPage />} />
        <Route path="/patients/:id" element={<PatientRegistrationPage />} />
        <Route path="/patients/:id/visits" element={<VisitHistoryPage />} />
        <Route path="/patients/:id/prescriptions" element={<PrescriptionHistoryPage />} />
        
        {/* Visit Routes */}
        <Route path="/visits/new" element={<NewVisitPage />} />
        <Route path="/visits/:id" element={<VisitHistoryPage />} />
        <Route path="/visits/:id/vitals" element={<VitalsForm />} />
        
        {/* Prescription Routes */}
        <Route path="/prescriptions/new" element={<PrescriptionPage />} />
        <Route path="/prescriptions/:id" element={<PrescriptionView />} />
        <Route path="/prescriptions/:id/edit" element={<PrescriptionPage />} />
        
        {/* Clinical Workflow Routes */}
        <Route path="/workflow" element={<ClinicalWorkflowPage />} />
        <Route path="/workflow/patient-select" element={<ClinicalWorkflowPage />} />
        <Route path="/workflow/vitals" element={<ClinicalWorkflowPage />} />
        <Route path="/workflow/prescription" element={<ClinicalWorkflowPage />} />
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/clinical" replace />} />
      </Routes>
    // </ClinicalLayout>
  );
};

// Main Feature Component
const ClinicalFeature = () => {
  return (
    <ClinicalProvider>
      <ClinicalContent />
    </ClinicalProvider>
  );
};

// Export all components for external use
export {
  // Pages
  ClinicalDashboard,
  PatientRegistrationPage,
  NewVisitPage,
  PrescriptionPage,
  ClinicalWorkflowPage,
  PatientListPage,
  VisitHistoryPage,
  PrescriptionHistoryPage,
  
  // Components
  PatientRegistration,
  PatientSearch,
  PatientList,
  VisitCreation,
  VitalsForm,
  VisitHistory,
  PrescriptionCreation,
  MedicineSelector,
  TestSelector,
  PrescriptionView,
  DoctorNotes,
  Timeline,
  LoadingSpinner,
  
  // Context and Hooks
  ClinicalProvider,
  useClinical
};

// Export services and utilities
export { default as clinicalApi } from './services/clinicalApi';
export * from './services/clinicalMockData';
export * from './utils/clinicalHelpers';

// Export as default
export default ClinicalFeature;